import React, { useState, useEffect } from "react";
import FeedCard from "./components/FeedCard";
import PostView from "./components/PostView";

export default function App() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPost, setExpandedPost] = useState(null);

  // Admin auth state
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [adminPassword, setAdminPassword] = useState(null);
  const [authError, setAuthError] = useState("");
  const [unlocking, setUnlocking] = useState(false);

  // Compose form state
  const [composeTitle, setComposeTitle] = useState("");
  const [composeContent, setComposeContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    fetchPosts();
  }, []);

  // Load all posts from the API on mount
  const fetchPosts = async () => {
    try {
      const res = await fetch("/api/posts");
      const data = await res.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load posts", e);
    } finally {
      setLoading(false);
    }
  };

  // Validate the admin password against the server
  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!passwordInput.trim() || unlocking) return;
    setUnlocking(true);
    setAuthError("");

    try {
      const res = await fetch("/api/auth", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: passwordInput }),
      });

      if (res.status === 401) {
        setAuthError("Wrong password.");
        return;
      }

      if (!res.ok) {
        setAuthError("Auth check failed — try again.");
        return;
      }

      // Password confirmed — store it and close the gate
      setAdminPassword(passwordInput);
      setPasswordInput("");
      setShowPasswordInput(false);
    } catch (e) {
      setAuthError("Couldn't reach server. Try again.");
    } finally {
      setUnlocking(false);
    }
  };

  // Submit a new post to the API
  const handlePost = async (e) => {
    e.preventDefault();
    if (!composeContent.trim() || submitting) return;
    setSubmitting(true);
    setSubmitError("");

    try {
      const res = await fetch("/api/posts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password: adminPassword,
          title: composeTitle,
          content: composeContent,
        }),
      });

      if (res.status === 401) {
        // Session expired or password changed — force re-auth
        setAdminPassword(null);
        setShowPasswordInput(true);
        setAuthError("Wrong password — try again.");
        return;
      }

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Server error (${res.status})`);
      }

      const newPost = await res.json();
      setPosts((prev) => [newPost, ...prev]);
      setComposeTitle("");
      setComposeContent("");
    } catch (e) {
      setSubmitError(e.message || "Failed to post. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete a post by id
  const handleDelete = async (postId) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      const res = await fetch("/api/posts", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: adminPassword, id: postId }),
      });
      if (res.ok) {
        setPosts((prev) => prev.filter((p) => p.id !== postId));
        if (expandedPost && expandedPost.id === postId) setExpandedPost(null);
      }
    } catch (e) {
      console.error("Delete failed", e);
    }
  };

  // Clear admin session and reset the compose form
  const exitAdmin = () => {
    setAdminPassword(null);
    setComposeTitle("");
    setComposeContent("");
    setSubmitError("");
  };

  // If a post is expanded, show the full post view instead of the feed
  if (expandedPost) {
    return (
      <PostView
        post={expandedPost}
        onBack={() => setExpandedPost(null)}
        isAdmin={!!adminPassword}
        onDelete={() => handleDelete(expandedPost.id)}
      />
    );
  }

  return (
    <div className="page">
      {/* Header */}
      <header className="site-header">
        <div className="header-inner">
          <span className="site-title">Evan Zone</span>
          <div className="header-actions">
            {adminPassword ? (
              <button className="admin-exit-btn" onClick={exitAdmin}>
                ✕ Exit
              </button>
            ) : (
              <button
                className="write-btn"
                onClick={() => {
                  setShowPasswordInput((s) => !s);
                  setAuthError("");
                }}
              >
                Write
              </button>
            )}
          </div>
        </div>

        {/* Password gate — shown when "Write" is clicked */}
        {showPasswordInput && !adminPassword && (
          <div className="password-gate">
            <form className="password-form" onSubmit={handleUnlock}>
              <input
                type="password"
                className="password-input"
                placeholder="Password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                autoFocus
              />
              <button type="submit" className="password-submit" disabled={unlocking}>
                {unlocking ? "Checking…" : "Unlock"}
              </button>
            </form>
            {authError && <p className="auth-error">{authError}</p>}
          </div>
        )}
      </header>

      <main className="feed-main">
        {/* Compose box — admin only */}
        {adminPassword && (
          <div className="compose-box">
            <form onSubmit={handlePost}>
              <input
                type="text"
                className="compose-title-input"
                placeholder="Title (optional)"
                value={composeTitle}
                onChange={(e) => setComposeTitle(e.target.value)}
              />
              <textarea
                className="compose-textarea"
                placeholder="What's on your mind? Markdown supported."
                value={composeContent}
                onChange={(e) => setComposeContent(e.target.value)}
                rows={5}
              />
              <div className="compose-footer">
                <span className="compose-hint">Markdown supported</span>
                <button
                  type="submit"
                  className="post-submit-btn"
                  disabled={submitting || !composeContent.trim()}
                >
                  {submitting ? "Posting…" : "Post"}
                </button>
              </div>
              {submitError && <p className="submit-error">{submitError}</p>}
            </form>
          </div>
        )}

        {/* Feed */}
        {loading && <div className="feed-status">Loading…</div>}

        {!loading && posts.length === 0 && (
          <div className="feed-empty">Nothing here yet.</div>
        )}

        <div className="post-feed">
          {posts.map((post) => (
            <FeedCard
              key={post.id}
              post={post}
              isAdmin={!!adminPassword}
              onExpand={() => setExpandedPost(post)}
              onDelete={() => handleDelete(post.id)}
            />
          ))}
        </div>
      </main>

      <footer className="site-footer">
        <span>© {new Date().getFullYear()} Evan Zone</span>
      </footer>
    </div>
  );
}
